/**
 * Where "back" goes from each screen of the main app — one table for the
 * hardware button, the edge swipe and the on-screen back buttons that defer
 * to it. Three screens had none, so the hardware button did nothing there,
 * and the order detail's own button always went to the Orders list, even
 * when the order had been opened from an operation.
 *
 * `origins` are the screens each one was opened from, as App tracks them.
 * null: the root — there is nowhere to go back to.
 */
export interface BackOrigins {
  balanceDetail: string;
  depositAccount: string;
  investmentAccount: string;
  openDeposit: string;
  transaction: string;
  operations: string;
  tokenDetail: string;
  profile: string;
  reports: string;
  historyDetail: string;
  orderDetail: string;
}

/** What else decides where "back" goes. */
export interface BackContext {
  /** A deposit top-up or withdrawal opened by "Repeat" on an order: back is the order. */
  repeatingOrder?: boolean;
}

export const backTargetFor = (tab: string, origins: BackOrigins, context: BackContext = {}): string | null => {
  switch (tab) {
    case 'balanceDetail':
      return origins.balanceDetail;
    case 'depositAccount':
      return origins.depositAccount;
    case 'investmentAccount':
      return origins.investmentAccount;
    case 'openDeposit':
      return origins.openDeposit;
    case 'topupDeposit':
    case 'outflowDeposit':
      // Repeating an order, back is the order — as the screen's own button.
      return context.repeatingOrder ? 'orderDetail' : 'depositAccount';
    case 'closeDeposit':
      return 'depositAccount';
    case 'cardHistory':
    case 'cardInfo':
      return 'cardDetail';
    case 'cardBlock':
      // Blocking is asked for on the card-details screen, and goes back there.
      return 'cardInfo';
    case 'transactionDetail':
      return origins.transaction;
    case 'operations':
      return origins.operations;
    case 'qrScanner':
      return 'operations';
    case 'tokenDetail':
      return origins.tokenDetail;
    case 'profile':
      return origins.profile;
    case 'changePassword':
    case 'changePIN':
    case 'pinSetup':
    case 'userSessions':
      return 'profile';
    case 'reports':
      return origins.reports;
    case 'historyDetail':
      return origins.historyDetail;
    case 'orderDetail':
      return origins.orderDetail;
    case 'cardDetail':
    case 'noCard':
    case 'transferSuccess':
    case 'allAssets':
    case 'popularTokens':
    case 'notifications':
    case 'history':
    case 'orders':
    case 'support':
    case 'products':
      return 'dashboard';
    default:
      return null; // 'dashboard' — the root
  }
};

/**
 * Where "back" actually lands. The operation and the order details render
 * their list when nothing is picked, and their origins point at each other
 * once a receipt was opened from an order: operation → order → receipt →
 * back → back left the user going round between the two with nothing picked,
 * and only the tab bar got them out. A details screen with nothing to show
 * is replaced by its list, which goes back to the dashboard.
 */
export const backLandingFor = (
  target: string,
  picked: { historyRecord: boolean; request: boolean },
): string => {
  if (target === 'historyDetail' && !picked.historyRecord) {
    return 'history';
  }
  if (target === 'orderDetail' && !picked.request) {
    return 'orders';
  }
  return target;
};
